/**
 * F13.3 + F14.3 — Login refatorado para design system v2.1 + i18n.
 * Consome componentes compartilhados em `components/auth/AuthShell`.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Redirect, useLocation } from "wouter";
import {
  AlertError,
  AuthBrand,
  AuthShell,
  FieldLabel,
  GhostLink,
  PillInput,
  PrimaryButton,
} from "../components/auth/AuthShell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useT } from "../i18n/index.js";
import { trpc } from "../lib/trpc.js";

/**
 * F-acesso-por-menu (CEO 2026-10-12) — destino pós-login: quem é SÓ de
 * portal (técnico / gerente de chantier / cliente) cai no portal dele, mesmo
 * tendo menus do Gestion liberados; os demais vão ao Painel.
 */
function landingFor(
  user: { isSuperAdmin: boolean; memberships: Array<{ active: boolean; role: string }> } | null,
): string {
  if (!user || user.isSuperAdmin) return "/dashboard";
  const roles = user.memberships.filter((m) => m.active).map((m) => m.role);
  if (roles.length === 0) return "/dashboard";
  if (roles.every((r) => r === "technician")) return "/portal";
  if (roles.every((r) => r === "manager")) return "/chantier";
  if (roles.every((r) => r === "client")) return "/cliente";
  return "/dashboard";
}

export default function Login() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user, isLoading, refetch } = useAuth();
  const t = useT();

  const loginMutation = trpc.auth.login.useMutation({
    onSuccess: async () => {
      // Purga cache da sessão anterior antes de carregar dados do novo user.
      queryClient.clear();
      const me = await refetch();
      // F-acesso-por-menu — quem é só de portal cai no portal dele, mesmo
      // com menus do Gestion liberados (de lá tem o botão "Abrir o Gestion").
      setLocation(landingFor(me.data ?? null));
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "var(--navy)", color: "var(--text-secondary)" }}
      >
        <p style={{ fontFamily: "var(--font-body)" }}>{t("common.loading")}</p>
      </div>
    );
  }
  if (user) return <Redirect to={landingFor(user)} />;

  const pending = loginMutation.isPending;

  return (
    <AuthShell>
      <form
        className="w-full max-w-[430px]"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          loginMutation.mutate({ email, password });
        }}
      >
        <AuthBrand subtitle={t("auth.subtitle")} />

        <FieldLabel htmlFor="email">{t("auth.emailLabel")}</FieldLabel>
        <PillInput
          id="email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={setEmail}
          focused={emailFocused}
          onFocus={() => setEmailFocused(true)}
          onBlur={() => setEmailFocused(false)}
          disabled={pending}
          autoFocus
          placeholder={t("auth.emailPlaceholder")}
        />

        <div className="mt-5">
          <FieldLabel htmlFor="password">{t("auth.passwordLabel")}</FieldLabel>
          <PillInput
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={setPassword}
            focused={passwordFocused}
            onFocus={() => setPasswordFocused(true)}
            onBlur={() => setPasswordFocused(false)}
            disabled={pending}
            placeholder={t("auth.passwordPlaceholder")}
          />
        </div>

        {error && (
          <div className="mt-5">
            <AlertError>{error}</AlertError>
          </div>
        )}

        <div className="mt-7">
          <PrimaryButton disabled={pending}>
            {pending ? t("auth.submitLoginPending") : t("auth.submitLogin")}
          </PrimaryButton>
        </div>

        <div className="mt-6">
          <GhostLink href="/esqueci-senha">{t("auth.forgotPasswordLink")}</GhostLink>
        </div>
        {/* F-tech-signup — quem ainda não tem conta se cadastra por aqui. */}
        <div className="mt-3">
          <GhostLink href="/cadastro-tecnico">{t("landing.techSignupLink")}</GhostLink>
        </div>
      </form>
    </AuthShell>
  );
}
